import { cacheKeys } from "@constants/cache-keys";
import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { useAppStore } from "@hooks/useAppStore";
import type { BaseItemSchema, ReportDetails } from "@memorang/types";
import { useQueryClient } from "@tanstack/react-query";
import { useLocalSearchParams } from "expo-router";
import React from "react";
import { usePrinting } from "../../hooks/usePrinting";

const PrintableReport = React.lazy(() => import("./PrintableReport"));

export default function PrintableReportContainer() {
	const { handleCancelPrint } = usePrinting();

	const queryClient = useQueryClient();
	const { id } = useLocalSearchParams<{ id: string }>();
	const {
		userPublicMetadataId: userId,
		email,
		userLogicalId,
	} = useAuthContext();

	const {
		currentExamContext: { examName },
	} = useExamContext();

	const cachedData = queryClient.getQueryData<BaseItemSchema[]>([
		cacheKeys.sessionItems,
		Number(id),
	]);

	const appName = useAppStore((state) => state.app.name);

	const reportDetails = queryClient.getQueryData<ReportDetails>([
		"report",
		id,
		userId,
	]);

	const assessmentName = reportDetails?.examName;
	const assessmentId = reportDetails?.numericId;

	if (!(examName && assessmentId && email && id && userLogicalId)) {
		return null;
	}

	const data = {
		id: userLogicalId!,
		email: email,
		assessment: assessmentName,
		assessmentId: assessmentId,
		appName,
		exam: examName,
		accuracy: `${reportDetails?.accuracy ?? 0}%`,
		tags: reportDetails?.tagDetails ?? [],
		items: cachedData ?? [],
		numericId: reportDetails?.numericId,
	};

	return (
		<React.Suspense fallback={null}>
			<PrintableReport data={data} onCancel={handleCancelPrint} />
		</React.Suspense>
	);
}
